import { useCallback, useEffect, useState } from 'react';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import CustomizeModal from './components/CustomizeModal';
import Filters from './containers/Filters';
import What from './containers/What';
import When from './containers/When';
import Where from './containers/Where';
import Who from './containers/Who';
import MultiDeparture from './containers/MultiDeparture';
import { useDispatch, useSelector } from 'react-redux';
import { selectFilterData } from '../mainFilter/selectors';
import { selectSecondaryFilter, updateAllSecondaryFilter } from '../secondaryFilter/secondaryFilterSlice';
import { parse } from 'date-fns';
import { updateBudgetFilter, updateDatesFilter, updateMainFilter, updateWhoFilter } from '../mainFilter/mainFilterSlice';
import { scrollToResults } from '../../utilities/helpers';
import format from '../../utilities/format';
import { clearSelectedHotel, clearStaticDeal, fetchHotelById, fetchStaticHotelById, setSelectedHotel } from '../search/searchSlice';
import { setLastChangedFilter } from '../../appSlice';
import { setDataV2 } from '../wizard-v2/onboardingSlice';
import { t } from '../../utilities/translator';
import SearchByHotel from './containers/SearchByHotel';
import { pushEvent } from '../../utilities/analytics';
import { CUSTOMIZE_UPDATE_FEED_CLICKED, CUSTOMIZE_START_WIZARD, CUSTOMIZE_FILTERS_CLICKED } from '../../constants/analyticEvents';
import useWarmupDeals from '../../hooks/app/useWarmupDeals';
import { updateSearch } from '../mainFilter/searchSlice';
import { MARKET } from '../../constants';
import Button from '../../components/button/Button';
import { clearLocationDeals } from '../locationDeals/locationDealsSlice';

const CustomizePanel = ({ show, close }) => {
  const dispatch = useDispatch();
  const { searchEngine } = useSelector(selectFilterData);
  const { what, when, who, where, fromwhere, budget } = searchEngine;
  const { selectedHotel: stateSelectedHotel, ...search } = useSelector(state => state.userSearch);
  const { data } = useSelector(selectSecondaryFilter);
  const [whatSelection, setWhatSelection] = useState([]);
  const [whereSelection, setWhereSelection] = useState({
    destinations: [],
    destinationGroups: [],
  });
  const [showFilters, setShowFilters] = useState(false);
  const [whenSelection, setWhenSelection] = useState({});
  const [whoSelection, setWhoSelection] = useState({});
  const [filtersSelection, setFiltersSelection] = useState({});
  const [departFrom, setDepartFrom] = useState(fromwhere);
  const [nightsSelection, setNightsSelection] = useState(when.nights || []);
  const [budgetSelection, setBudgetSelection] = useState();
  const [hotelSelection, setHotelSelection] = useState(stateSelectedHotel || null);
  useBodyScrollLock({ condition: show, mobileOnly: true });
  const { warmupDealsCache } = useWarmupDeals(true);

  const [updateFilterAndClose, setUpdateFilterAndClose] = useState(false);

  const updateFilterFromMainState = useCallback(() => {
    const children = who.childAges.filter(i => i > 1).length;
    setWhatSelection(what?.filter(item => item.active).map(item => item.id));
    setWhereSelection({
      destinations: where.destinations?.filter(item => item.active).map(item => item.id),
      destinationGroups: where.destinationsGroups?.filter(item => item.active).map(item => item.id),
    });
    setWhenSelection({
      type: when.type || 'month',
      startDate: when.startDate ? parse(when.startDate, 'dd/MM/yyyy', new Date()) : null,
      endDate: when.endDate ? parse(when.endDate, 'dd/MM/yyyy', new Date()) : null,
      months: when.months || [],
      nights: when.nights || [],
      weekendsOnly: when.weekendsOnly
    });
    setWhoSelection({
      adults: who.adults,
      children: children,
      rooms: who.rooms,
      childAges: who.childAges,
      babies: who.childAges.length - children,
    });
    setNightsSelection(when.nights || []);
    setDepartFrom(fromwhere);
    setFiltersSelection(data);
    setBudgetSelection([budget.min, budget.max]);
    setHotelSelection(stateSelectedHotel);
  }, [what, when, who, where, fromwhere, data, budget, stateSelectedHotel])

  // Update state if filters were changed from other place
  useEffect(() => {
    updateFilterFromMainState()
  }, [updateFilterFromMainState])

  const getReduxCompatibleWhen = () => {
    // When filter update. should look better but I have no time to refactor.
    let dates;
    switch (whenSelection.type) {
      case 'month':
        dates = {
          type: whenSelection.type,
          months: whenSelection.months,
          minNights: nightsSelection?.length > 0 ? Math.min(...nightsSelection) : undefined,
          maxNights: nightsSelection?.length > 0 ? Math.max(...nightsSelection) : undefined,
          nights: nightsSelection || [],
          weekendsOnly: whenSelection.weekendsOnly,
        };
        break;

      case 'specific':
        dates = {
          type: whenSelection.type,
          startDate: whenSelection.startDate,
          endDate: whenSelection.endDate,
        };
        break;

      case 'range':
        dates = {
          type: whenSelection.type,
          startDate: whenSelection.startDate,
          endDate: whenSelection.endDate,
          weekendsOnly: whenSelection.weekendsOnly,
          minNights: nightsSelection?.length > 0 ? Math.min(...nightsSelection) : undefined,
          maxNights: nightsSelection?.length > 0 ? Math.max(...nightsSelection) : undefined,
          nights: nightsSelection || [],
        };
        break;

      default:
        dates = {
          type: whenSelection.type,
          startDate: whenSelection.startDate,
          endDate: whenSelection.endDate,
        };
        break;
    }

    if (dates.startDate instanceof Date) {
      dates.startDate = format(whenSelection.startDate, 'dd/MM/yyyy');
    }

    if (dates.endDate instanceof Date) {
      dates.endDate = format(whenSelection.endDate, 'dd/MM/yyyy');
    }

    return dates;
  }

  const getReduxCompatibleWho = () => {
    const childAges = whoSelection.childAges.filter(i => i > 1);
    const children = childAges.length;

    const data = {
      ...whoSelection,
      children: children + whoSelection.babies,
      childAges: childAges.concat(new Array(whoSelection.babies).fill(1)),
    }

    return data;
  }

  const getReduxCompatibleFilters = () => {
    const mainFilter = {
      what: what.map(i => ({ ...i, active: whatSelection.includes(i.id) })),
      where: {
        destinations: where.destinations.map(i => ({ ...i, active: whereSelection.destinations.includes(i.id) })),
        destinationsGroups: where.destinationsGroups.map(i => ({ ...i, active: whereSelection.destinationGroups.includes(i.id) })),
      },
      when: getReduxCompatibleWhen(),
      who: getReduxCompatibleWho(),
      budget: budgetSelection,
      fromwhere: departFrom,
    }

    if (what.find(i => i.id === 82)?.active && !whatSelection.find(i => [82, 88].includes(i)) && budget.max === 399  && MARKET === 1) {
      mainFilter.budget = [100, 3000];
    }

    if (what.find(i => i.id === 83)?.active && !whatSelection.find(i => i === 83) && budget.max === 499) {
      mainFilter.budget = [100, 3000];
    }

    if (whatSelection.find(i => [82, 88].includes(i))  && MARKET === 1) {
      mainFilter.budget = [100, 399];
    }

    if (whatSelection.find(i => i === 83)) {
      mainFilter.budget = [100, 499];
    }

    const secondaryFilter = {
      ...filtersSelection,
    }

    return {
      mainFilter,
      secondaryFilter,
    }
  }

  const prefetchDeals = () => {
    const { mainFilter, secondaryFilter } = getReduxCompatibleFilters();
    warmupDealsCache({
      ...mainFilter,
      ...secondaryFilter,
      hotel: hotelSelection,
    })
  }

  const handleWhatChange = (selection) => {
    setWhatSelection(selection);
    setWhereSelection({
      destinations: [],
      destinationGroups: [],
    });
    setHotelSelection(null);
  }

  const handleWhereChange = (selection) => {
    setWhereSelection({
      ...whereSelection,
      ...selection
    });
    setHotelSelection(null);
  }

  const handleWhenChange = (selection) => {
    setWhenSelection({
      ...selection
    })

    if (selection.nights) {
      if (selection.type === 'specific') {
        setNightsSelection([]);
      } else {
        setNightsSelection(selection.nights);
      }
    }
  }

  const handleWhoSave = (selection) => {
    setWhoSelection(selection)
  }

  const handleDepartureSave = (selection) => {
    setDepartFrom(selection);
  };

  const handleFiltersChange = ({ filters, nights, budget, updateAndClose }) => {
    setFiltersSelection(filters);
    setNightsSelection(nights);
    setWhenSelection({
      ...whenSelection,
      nights,
    });
    setBudgetSelection(budget);
    setHotelSelection(null);
    if (updateAndClose) {
      setUpdateFilterAndClose(true);
    }
    handleToggleFilters();
  }

  const handleUpdateFeed = () => {
    dispatch(clearSelectedHotel());
    const { mainFilter } = getReduxCompatibleFilters();

    dispatch(updateMainFilter({
      filter: 'what',
      updatedValue: mainFilter.what
    }));
    dispatch(updateMainFilter({
      updatedValue: mainFilter.where,
      filter: 'where',
    }));

    dispatch(updateMainFilter({ filter: 'fromwhere', updatedValue: departFrom }));
    dispatch(updateDatesFilter(mainFilter.when));
    dispatch(updateWhoFilter(mainFilter.who));
    dispatch(updateBudgetFilter(mainFilter.budget));
    dispatch(updateSearch({
      when: mainFilter.when,
      who: mainFilter.who,
    }));
    dispatch(clearLocationDeals());

    dispatch(updateAllSecondaryFilter({ updatedFilters: filtersSelection }));
    if (hotelSelection?.hotelId || hotelSelection?.hfHotelId) {
      dispatch(setSelectedHotel(hotelSelection));
      if (+search.staticDeal?.id !== hotelSelection.hotelId) {
        dispatch(clearStaticDeal());
      }
      if (!hotelSelection.hfDestinationId && hotelSelection.hotelId) {
        dispatch(fetchHotelById(hotelSelection.hotelId));
      }

      dispatch(fetchStaticHotelById({
        id: hotelSelection.hotelId,
        destination: hotelSelection.hfDestinationId
      }));
    }
    dispatch(setLastChangedFilter('customize-panel'));
    pushEvent(CUSTOMIZE_UPDATE_FEED_CLICKED, {
      departFrom: departFrom.filter(item => item.active)?.map(dep => dep.id),
      what: mainFilter.what.filter(i => i.active),
      where: {
        destinations: mainFilter.where.destinations.filter(i => i.active),
        destinationsGroups: mainFilter.where.destinationsGroups.filter(i => i.active),
      },
      when: mainFilter.when,
      who: mainFilter.who,
      filters: {
        ...filtersSelection,
        stops: filtersSelection.stops.filter(i => i.active).map(i => i.value),
        stars: filtersSelection.stars.filter(i => i.active).map(i => i.value),
        sort: filtersSelection.sort.find(i => i.active).label,
      },
      budget: budgetSelection,
      hotel: hotelSelection
    });
    close();
    scrollToResults();
  }

  const handleStartWizard = () => {
    pushEvent(CUSTOMIZE_START_WIZARD);
    const { mainFilter } = getReduxCompatibleFilters();
    dispatch(setDataV2({
      who: {
        capacity: mainFilter.who
      }
    }));
    window.location.href = window.location.origin + '?startWizard=true#startWizard=true';
    close();
  }

  const handleSelectHotel = (hotel) => {
    if (hotel === null) {
      setHotelSelection(null);
    } else {
      setHotelSelection(hotel);
      setFiltersSelection({
        ...filtersSelection,
        board: filtersSelection.board.map(i => ({ ...i, active: false })),
        facilities: filtersSelection.facilities.map(i => ({ ...i, active: false })),
        freeCancellation: false,
        stars: filtersSelection.stars.map(i => ({ ...i, active: false })),
        stops: filtersSelection.stops.map(i => ({ ...i, active: false })),
      })

      if (
        whereSelection.destinations.length > 1 ||
        (whereSelection.destinations.length === 1 && whereSelection.destinations[0].id !== hotel.hfDestinationId)) {
        setWhereSelection({
          destinationGroups: [],
          destinations: [hotel.hfDestinationId]
        })
      }
    }
  }

  const handleToggleFilters = () => {
    if (!showFilters) {
      pushEvent(CUSTOMIZE_FILTERS_CLICKED);
    }
    setShowFilters(!showFilters);
  }

  const handleClosePanel = () => {
    updateFilterFromMainState();
    setShowFilters(false);
    close();
  }

  useEffect(() => {
    if (updateFilterAndClose) {
      handleUpdateFeed();
      setUpdateFilterAndClose(false);
    }
  }, [updateFilterAndClose]);

  // background loading for offers
  useEffect(() => {
    if (show) {
      prefetchDeals();
    }
  }, [whenSelection, whoSelection, whereSelection, filtersSelection, departFrom, nightsSelection, budgetSelection, hotelSelection])

  const handleLikeClick = () => {
    const userContext = JSON.parse(localStorage.getItem('hf_user'));
    if (userContext && userContext?.user?.userLevel === 'USER_EMAIL' && userContext?.user?.user_auth) {
      window.open('/holidayfinder/favorites', '_blank')
    } else if (typeof openLoginModal !== 'undefined') {
      // eslint-disable-next-line no-undef
      openLoginModal("Favorites on card");
    } else {
      window.jQuery('#loginModal').modal('show');
    }
  };

  return (
    <CustomizeModal show={show} close={handleClosePanel} slideDirection='vertical'>
      <CustomizeModal.Header close={handleClosePanel} />
      <CustomizeModal.Body className="pb-7">
        <div className="panel-hot-buttons gap-3 pt-5">
          <button className="btn-start-wizard p-2 gap-2 label-medium !font-semibold w-full" onClick={handleStartWizard}>
            <i className="ri-bard-line" />
            {t('customize.startWizard')}
          </button>
          <Button
            type="text-link"
            label={<i className="ri-heart-3-line text-2xl leading-none" />}
            className="w-auto grow"
            onClick={handleLikeClick}
          />
        </div>

        <div className="customize-options gap-3">
          <MultiDeparture onSave={handleDepartureSave} selected={departFrom} />
          <What onSave={handleWhatChange} selected={whatSelection} capacity={whoSelection} />
          <Where onSave={handleWhereChange} selected={whereSelection} whatSelection={whatSelection} />
          <When onSave={handleWhenChange} selected={whenSelection} />
          <Who onSave={handleWhoSave} selected={whoSelection} />
        </div>
        {whereSelection.destinations.length > 0 || whereSelection.destinationGroups.length > 0 ? (
          <SearchByHotel
            destinationIds={whereSelection.destinations}
            onSelectHotel={handleSelectHotel}
            selectedHotel={hotelSelection}
          />
        ) : null}
      </CustomizeModal.Body>
      <CustomizeModal.Footer>
        <Button
          type="secondary"
          label={t('customize.filters')}
          startIcon={<i className="ri-equalizer-line text-lg leading-none" />}
          onClick={handleToggleFilters}
          className="w-auto grow"
          rounded
        />
        <Button
          onClick={handleUpdateFeed}
          label={t('customize.updateFeed')}
          size="lg"
          className="w-auto grow-2"
          opticalEnd
          opticalStart
        />
        <Filters
          onSave={handleFiltersChange}
          selected={filtersSelection}
          nightsSelection={nightsSelection}
          budgetSelection={budgetSelection}
          whenSelection={whenSelection}
          handleClosePanel={handleClosePanel}
          show={showFilters}
          onClose={handleToggleFilters}
        />
      </CustomizeModal.Footer>
    </CustomizeModal>
  )
}

export default CustomizePanel
